import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Package, Truck, Activity, CheckCircle, AlertTriangle, Loader2,
  MapPin, Clock, ArrowRight, HelpCircle, Phone
} from 'lucide-react';

import {
  getTrackingData,
  getStatusInfo,
  processTrackingHistory,
  getApiEndpoint,
  validateTrackingNumber
} from '../utils/trackingUtils';

// --- ANIMATION VARIANTS ---

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const expandResult = {
  hidden: { opacity: 0, height: 0, scale: 0.99 },
  visible: {
    opacity: 1,
    height: "auto",
    scale: 1,
    transition: { duration: 0.4, ease: "circOut" }
  },
  exit: { opacity: 0, height: 0, scale: 0.99, transition: { duration: 0.2 } }
};

const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// --- STYLED COMPONENTS ---

const PageContainer = styled.div`
  margin-top: 80px;
  min-height: calc(100vh - 80px);
  background-color: #F8FAFC;
  padding: 80px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;

  /* Subtle background pattern */
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; height: 300px;
    background: linear-gradient(180deg, #eef2ff 0%, transparent 100%);
    z-index: 0;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 700px;
  z-index: 10;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 48px;

  h1 {
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 16px;
    letter-spacing: -0.02em;
  }

  p {
    color: #64748b;
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

// --- SEARCH COMPONENT (Consistent with Home) ---

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  background: white;
  padding: 8px;
  border-radius: 100px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02);
  transition: all 0.3s ease;
  margin-bottom: 32px;

  &:focus-within {
    border-color: #6366f1;
    box-shadow: 0 20px 25px -12px rgba(99, 102, 241, 0.2);
    transform: translateY(-2px);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  padding: 16px 24px;
  font-size: 1.1rem;
  color: #0f172a;
  font-weight: 500;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }
`;

const SearchBtn = styled.button`
  background: #0f172a;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 14px 32px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s ease;

  &:hover {
    background: #334155;
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

const Spinner = styled(Loader2)`
  animation: ${spinAnimation} 1s linear infinite;
`;

// --- RESULT CARD STYLES ---

const ResultCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.025);
  border: 1px solid #e2e8f0;
`;

const ResultHeader = styled.div`
  padding: 24px 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  .id-group {
    h3 { font-size: 0.85rem; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; margin-bottom: 4px; }
    .number { font-size: 1.25rem; font-weight: 700; color: #0f172a; font-family: monospace; }
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: white;
    border: 1px solid #e2e8f0;
    color: #0f172a;
    padding: 8px 16px;
    border-radius: 50px;
    font-size: 0.9rem;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
  }
`;

const Timeline = styled.div`
  padding: 40px 32px;
  background: white;
`;

const TimelineItem = styled(motion.div)`
  display: flex;
  gap: 24px;
  position: relative;
  padding-bottom: 40px;

  &:last-child { padding-bottom: 0; }
  
  /* Connecting Line */
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 21px;
    top: 45px;
    bottom: 0;
    width: 2px;
    background: #e2e8f0;
  }

  .icon-box {
    position: relative;
    z-index: 2;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: ${props => props.$active ? '#0f172a' : '#ffffff'};
    border: 2px solid ${props => props.$active ? '#0f172a' : '#e2e8f0'};
    color: ${props => props.$active ? 'white' : '#94a3b8'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: ${props => props.$active ? '0 4px 10px rgba(0,0,0,0.1)' : 'none'};
  }

  .content {
    padding-top: 8px;
    flex: 1;

    h4 { 
      font-size: 1.1rem; 
      font-weight: 700; 
      color: #0f172a; 
      margin-bottom: 4px; 
    }
    
    .location {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 0.95rem; 
      color: #475569;
      margin-bottom: 6px;
    }

    .time { 
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 0.85rem; 
      color: #94a3b8; 
      background: #f8fafc;
      padding: 4px 10px;
      border-radius: 6px;
      font-weight: 500;
    }
  }
`;

const ErrorMessage = styled(motion.div)`
  margin-bottom: 24px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  color: #991b1b;
  padding: 16px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  justify-content: center;
`;

const HelpSection = styled(motion.div)`
  margin-top: 48px;
  text-align: center;
  padding: 24px;
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  width: 100%;

  h4 { font-size: 1.1rem; color: #0f172a; margin-bottom: 8px; font-weight: 700; }
  p { color: #64748b; font-size: 0.95rem; margin-bottom: 16px; }
  
  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #4f46e5;
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`;

// --- COMPONENT ---

const Tracking = () => {
  const [searchParams] = useSearchParams();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-search if URL has param
  useEffect(() => {
    const queryId = searchParams.get('trackingNumber');
    if (queryId) {
      setTrackingNumber(queryId);
      performSearch(queryId);
    }
  }, [searchParams]);

  const performSearch = async (id) => {
    if (!id || !id.trim()) return;

    if (!validateTrackingNumber(id)) {
      setError('Invalid format. Please use format like 123456SDD7890');
      setTrackingResult(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setTrackingResult(null);

    try {
      const localData = getTrackingData(id);
      if (localData) {
        setTrackingResult(localData);
      } else {
        const response = await fetch(`${getApiEndpoint('searchById')}/${id.trim()}`);
        const data = await response.json();
        if (data.success) {
          setTrackingResult(data.data);
        } else {
          setError(data.message || 'Shipment not found in our records.');
        }
      }
    } catch (err) {
      setError('Network connection error. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    performSearch(trackingNumber);
  };

  const getIcon = (iconName) => {
    const map = {
      'FaBox': Package,
      'FaTruck': Truck,
      'FaRunning': Activity,
      'FaCheckCircle': CheckCircle,
      'FaExclamationTriangle': AlertTriangle
    };
    return map[iconName] || Package;
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <Header initial="hidden" animate="visible" variants={fadeInUp}>
          <h1>Track Your Shipment</h1>
          <p>Real-time updates for your SDD deliveries.<br />Enter your ID below to get started.</p>
        </Header>

        <SearchForm onSubmit={handleSearch}>
          <SearchInput
            placeholder="Enter Tracking ID (e.g., 599663SDD2442)"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
          />
          <SearchBtn type="submit" disabled={isLoading}>
            {isLoading ? <Spinner size={20} /> : <Search size={20} />}
            <span>Track</span>
          </SearchBtn>
        </SearchForm>

        <AnimatePresence>
          {error && (
            <ErrorMessage initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <AlertTriangle size={20} /> {error}
            </ErrorMessage>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {trackingResult && (
            <ResultCard
              variants={expandResult}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <ResultHeader>
                <div className="id-group">
                  <h3>Tracking Number</h3>
                  <div className="number">{trackingResult.shipment_id}</div>
                </div>
                <div className="status-badge">
                  <Activity size={18} color="#4f46e5" />
                  {getStatusInfo(trackingResult.status).title}
                </div>
              </ResultHeader>

              <Timeline>
                {processTrackingHistory(trackingResult.history, trackingResult.status).map((step, idx) => {
                  const Icon = getIcon(step.icon);
                  return (
                    <TimelineItem
                      key={idx}
                      $active={step.isActive}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * idx }}
                    >
                      <div className="icon-box"><Icon size={20} /></div>
                      <div className="content">
                        <h4>{step.title}</h4>
                        <div className="location">
                          <MapPin size={14} /> {step.location || 'In Transit'}
                        </div>
                        <div className="time">
                          <Clock size={14} /> {step.time}
                        </div>
                      </div>
                    </TimelineItem>
                  );
                })}
              </Timeline>
            </ResultCard>
          )}
        </AnimatePresence>

        {/* HELPER SECTION */}
        <HelpSection initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
            <HelpCircle size={32} color="#94a3b8" />
          </div>
          <h4>Having trouble finding your package?</h4>
          <p>If you can't track your order or the status hasn't changed in 24 hours, our support team is here to help.</p>
          <a href="mailto:support@sddlogistics.com">
            <Phone size={16} /> Contact Support
          </a>
        </HelpSection>

      </ContentWrapper>
    </PageContainer>
  );
};

export default Tracking;